import {
  AfterViewInit,
  Component,
  OnInit,
  signal,
  effect,
  ElementRef,
  ViewChild,
  ViewEncapsulation,
  afterNextRender,
  Injector,
  inject
} from '@angular/core';
import { CommonModule } from '@angular/common'; // For *ngFor, *ngIf
import { FormsModule } from '@angular/forms'; // For ngModel
import { ChatApiService } from 'src/app/services/chatbot/chatbot.service';
import { ChatMessage } from 'src/models/interfaces/chatbot/chatbot.interface';
import { LoadingService } from 'src/app/services/loading/loading.service';
import { MaterialModule } from '../../material.module';
import { MAT_SNACK_BAR_DATA } from '@angular/material/snack-bar';
import { MarkdownBoldPipe } from './markdown-bold.pipe';
import { TextFieldModule } from '@angular/cdk/text-field';
import { Observable } from 'rxjs';

@Component({
  selector: 'spe-chat',
  standalone: true,
  imports: [CommonModule, FormsModule, MaterialModule, MarkdownBoldPipe, TextFieldModule],
  templateUrl: './chat.component.html',
  styleUrls: ['./chat.component.scss']
})
export class ChatComponent implements OnInit, AfterViewInit {
  @ViewChild('chatHistory') private chatHistoryContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('chatInput') private chatInput!: ElementRef<HTMLInputElement>;

  private injector = inject(Injector);
  private chatService = inject(ChatApiService);
  public readonly loadingService = inject(LoadingService);
  public data: { close: () => void } = inject(MAT_SNACK_BAR_DATA);
  public loading$?: Observable<boolean>;

  messages = signal<ChatMessage[]>([]);
  userInput = signal<string>('');
  isLoading = signal<boolean>(false);
  isInitialMessage = signal<boolean>(true);
  readonly maxLength = 400;
  exampleMessages = signal<string[]>([
    'Como faço para gerar um pedido?',
    'Como faço para pagar um pedido?',
    'Como faço para acompanhar um pedido?',
    'Como faço para mudar minha senha?'
  ]);
  contactUsMessages = signal<{ label: string; value: string }[]>([
    { label: 'Whatsapp', value: '11 3004-4242' },
    { label: 'Email', value: 'atendimento.beneficios@semparar.com.br' }
  ]);

  constructor() {
    effect(() => {
      this.messages();
      this.isLoading();
      afterNextRender(() => this.scrollToBottom(), { injector: this.injector });
    });
  }

  ngOnInit(): void {
    this.loading$ = this.loadingService.loading$;
    // Initial welcome message or load history
    this.messages.set([
      {
        text: 'Olá! Sou o **Assistente IA da Sem Parar Empresas** e estou em fase beta.\n\nPosso ajudar com dúvidas sobre benefícios, cartões, emissores, funcionalidades e navegação na plataforma.\n\nEstou em constante evolução e algumas respostas podem conter imprecisões. Fique à vontade para fazer sua pergunta.',
        sender: 'ai',
        timestamp: new Date()
      }
    ]);
  }

  ngAfterViewInit(): void {
    afterNextRender(() => this.chatInput?.nativeElement.focus(), { injector: this.injector });
  }

  handleContactUsMessage(message: { label: string; value: string }): void {
    const label = message.label.trim().toLowerCase();
    const value = message.value.trim();

    if (label === 'whatsapp') {
      const phone = value.replace(/\D/g, '');
      const url = `https://wa.me/+55${phone}`;
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }

    if (label === 'email') {
      window.location.href = `mailto:${value}`;
      return;
    }
  }

  sendMessage(message?: string): void {
    let currentInput = this.userInput().trim();
    if (!currentInput && !message) return;

    if (message) {
      currentInput = message;
    }

    if (this.isInitialMessage()) {
      this.isInitialMessage.set(false);
    }

    this.messages.update((msgs) => [...msgs, { text: currentInput, sender: 'user', timestamp: new Date() }]);
    this.userInput.set('');
    this.isLoading.set(true);

    this.chatService.sendMessage(currentInput).subscribe({
      next: (response) => {
        const isContactUsResponse =
          response.resposta.toLowerCase().includes('3004-4242') ||
          response.resposta.toLowerCase().includes('atendimento.beneficios@semparar.com.br');

        this.messages.update((msgs) => [
          ...msgs,
          {
            text: response.resposta,
            sender: 'ai',
            timestamp: new Date(),
            isContactUs: isContactUsResponse,
            idMessage: response.idMessage
          }
        ]);

        this.isLoading.set(false);
      },
      error: (err) => {
        console.error('Error getting AI response:', err);

        this.messages.update((msgs) => [
          ...msgs,
          {
            text: 'Essa informação não está disponível no momento.\n\n Por favor, entre em contato pelo nosso WhatsApp 11 3004-4242 ou nosso email atendimento.beneficios@semparar.com.br.',
            sender: 'ai',
            timestamp: new Date(),
            isContactUs: true,
            idMessage: err?.error?.idMessage
          }
        ]);
        this.isLoading.set(false);
      }
    });
  }

  informacaoUtil(message: ChatMessage, event?: Event): void {
    event?.stopPropagation();
    this.enviarFeedback(message, true);
  }

  informacaoNaoUtil(message: ChatMessage, event?: Event): void {
    event?.stopPropagation();
    this.enviarFeedback(message, false);
  }

  private enviarFeedback(message: ChatMessage, flUtil: boolean): void {
    if (!message.idMessage || message.feedback) {
      return;
    }

    const request$ = flUtil
      ? this.chatService.informacaoUtil(message.idMessage)
      : this.chatService.informacaoNaoUtil(message.idMessage);

    request$.subscribe({
      next: () => {
        this.messages.update((msgs) =>
          msgs.map((msg) =>
            msg === message ? { ...msg, feedback: flUtil ? 'informacaoUtil' : 'informacaoNaoUtil' } : msg
          )
        );
      },
      error: (err) => {
        console.error('Error sending feedback:', err);
      }
    });
  }

  public handleLinkClick = (event: Event) => {
    event.stopPropagation();
    event.preventDefault();
    const target = (event.target as HTMLElement).closest('[data-url]') as HTMLElement | null;
    const url = target?.dataset['url'];
    if (url) {
      window.open(url);
    }
  };

  closeChat(): void {
    this.data.close();
  }

  private scrollToBottom(): void {
    if (this.chatHistoryContainer) {
      const element = this.chatHistoryContainer.nativeElement;
      element.scrollTop = element.scrollHeight;
    }
  }
}
